import { PatientProduct } from "services/types/patient-products.type";
import { CSVReportResponse } from "services/types/reports.type";
import { formatValidQueryParamUrlCommaSeparated } from "utils/common";
import {
  formatPatient,
  formatPatientFilters,
  getPatientsEndpointWithQueryParameters
} from "utils/patients.util";
import { createPaginatedEndpoint } from "utils/services.util";

import { PaginatedResponse } from "./common";
import { emptySplitApi } from "./empty-split-api";
import {
  Card,
  DeactivatePatientInput,
  DeactivatePatientResponse,
  HearingLossSeverityOptions,
  Patient,
  PatientFilters,
  PatientInput,
  PatientMergeDetails,
  PatientPhotoInput,
  PatientSummary,
  PatientSummaryType,
  PatientsDownloadQuery,
  PatientsResponse,
  QuickBooksCustomerDetails,
  SimilarPatient
} from "./types/patients.type";
import { QuickBooksInvoicesResponse } from "./types/quickbooks-invoice.type";

export const DEFAULT_PATIENTS_LIMIT = 25;
export const DEFAULT_PATIENTS_QUERY =
  "{id,first_name,middle_name,last_name,full_name,is_active,primary_care_physician{full_name},referring_physician{full_name},referral_source{id,name,referred_type},sub_referral_source{id,name,object_id,object_type,parent{id}},preferred_name,title,birthdate,age_years,photo,suffix,email,phone,secondary_phone,crm_segment,box_folder_id,unread_inbound_message_count,preferred_communication_method,phone_type,primary_contact_notes,secondary_contact_notes,city,state,preferred_clinic{id,name,display_name,timezone,region{id,name}},preferred_provider{id,first_name,last_name,full_name,title,suffix,is_active},previous_appointment_moment,next_appointment_moment,previous_appointment{id,type{name},status,start_moment,clinic{timezone},patient_feedback_rating,staff_member{first_name,last_name}},following_appointment_moment,following_appointment{id,type{id,name},needs_reschedule,title,start_moment,clinic{name,timezone},status,patient_feedback_rating,staff_member{first_name,last_name}},next_appointment{id,type{name},status,start_moment,clinic{timezone},patient_feedback_rating,staff_member{first_name,last_name}},managed_care_plan{id,name,notes,managed_care_provider{name}},assigned_tags,created_at_display,last_accessed,do_not_email,do_not_message}";

// SN-19556. Composed onto whichever base query a caller already picked, rather than a fourth
// `*_WITH_*` constant: pronouns already doubles each of these strings, and enumerating the
// cross-product would double them again on the next flag.
//
// This MUST stay flag-gated at every call site. RestQL raises `FieldNotFound` for an unknown
// read key, so requesting these against a backend that predates the field 400s the whole
// patient GET.
export const SECONDARY_PROVIDER_QUERY_FIELDS =
  "secondary_preferred_provider{id,first_name,last_name,full_name,title,suffix,is_active},secondary_preferred_provider_role";

// Appends `fields` inside the outermost `{...}` of a RestQL query when `enabled`. Every
// flag-gated read key goes through this so the splice lives in one place.
export const withQueryFields = (
  query: string,
  fields: string,
  enabled: boolean
) => (enabled ? `${query.slice(0, query.lastIndexOf("}"))},${fields}}` : query);

export const withSecondaryProviderFields = (query: string, enabled: boolean) =>
  withQueryFields(query, SECONDARY_PROVIDER_QUERY_FIELDS, enabled);

// ENG-1912. `DEFAULT_PATIENTS_QUERY` already carries `city,state`; the link-time "use address from
// web request?" check needs all four primary address fields, so the two missing ones are composed on
// at that single call site (flag-gated) rather than widening the shared query for every consumer.
export const PATIENT_ADDRESS_QUERY_FIELDS = "street_address_1,zip_code";

// ENG-1949. The header's card-on-file pill reads `may_have_cards_on_file` (retrieve-only) as its
// skeleton hint. Composed on only when the flag is on: an unknown RestQL field 400s the whole
// patient fetch on a backend that predates it, so the key must never be requested unconditionally.
export const CARDS_ON_FILE_QUERY_FIELDS = "may_have_cards_on_file";

export const withCardsOnFileFields = (query: string, enabled: boolean) =>
  withQueryFields(query, CARDS_ON_FILE_QUERY_FIELDS, enabled);

export const withPatientAddressFields = (query: string, enabled: boolean) =>
  withQueryFields(query, PATIENT_ADDRESS_QUERY_FIELDS, enabled);

// SN-19749. Flag-gated like the others: requesting these against a backend without the field 400s.
export const PREFERRED_LANGUAGE_QUERY_FIELDS =
  "preferred_language,preferred_language_other";

export const withPreferredLanguageFields = (query: string, enabled: boolean) =>
  withQueryFields(query, PREFERRED_LANGUAGE_QUERY_FIELDS, enabled);

// Texts not delivered (TEXTS_NOT_DELIVERED_FEATURE, Intercom 215476575290017). Read-only fields the
// backend stamps when a text fails, when it sets the phone type to Landline and when a STOP reply
// ticks Do not Text/SMS. Flag-gated like the others: an unknown RestQL key 400s the whole patient GET
// on a backend that predates them, so they are requested only with the flag on.
export const TEXTS_NOT_DELIVERED_QUERY_FIELDS =
  "sms_failed_at,sms_failed_is_open,sms_failed_reason_code,sms_failed_reason_label,sms_failed_reason_category,sms_failed_handled_at,phone_type_set_by_system_at,phone_type_set_by_system_reason,phone_type_set_by_system_from,phone_type_set_by_system_label,do_not_message_set_by_system_at,do_not_message_set_by_system_label,preferred_communication_method_set_by_system_at,preferred_communication_method_set_by_system_reason,preferred_communication_method_set_by_system_from,preferred_communication_method_set_by_system_label";

export const withTextsNotDeliveredFields = (query: string, enabled: boolean) =>
  withQueryFields(query, TEXTS_NOT_DELIVERED_QUERY_FIELDS, enabled);

export const DEFAULT_PATIENTS_QUERY_WITH_PRONOUNS =
  DEFAULT_PATIENTS_QUERY.replace(
    ",do_not_message}",
    ",do_not_message,pronouns}"
  );

// `phone_type` travels with `phone`: a surface that offers Text/SMS must know
// whether the number is a mobile, and an unrequested field is simply absent
// from the response — which reads as "not a mobile" and disables the option.
export const PATIENT_SEARCH_QUERY =
  "{id,first_name,middle_name,last_name,full_name,preferred_name,is_active,birthdate,age_years,email,phone,phone_type,secondary_phone,city,state,photo,preferred_provider{full_name},preferred_clinic{id,name,region{name}},box_folder_id}";

export const PATIENT_SEARCH_QUERY_WITH_ALERT_COUNT =
  "{id,first_name,middle_name,last_name,full_name,preferred_name,is_active,birthdate,age_years,email,phone,phone_type,secondary_phone,city,state,photo,preferred_provider{full_name},preferred_clinic{id,name,region{name}},box_folder_id,active_alert_count}";

export const PATIENT_RESPONSIBLE_PARTY_QUERY =
  "{id,full_name,first_name,middle_name,last_name,email,street_address_1,street_address_2,city,state,zip_code,responsible_party_relationship,responsible_party_first_name,responsible_party_last_name,responsible_party_street_address_1,responsible_party_street_address_2,responsible_party_city,responsible_party_state,responsible_party_country,responsible_party_zip_code,responsible_party_phone,responsible_party_email,bill_to_responsible_party,preferred_clinic{id,timezone,name,logo,display_name,surcharge_percent,effective_surcharge_percent,extra{billng_street_address_1,billng_street_address_2,billng_city,billng_state,billng_zip,billng_phone},street_address_1,street_address_2,city,state,zip_code,phone,region{id}}}";

export const patientsApi = emptySplitApi.injectEndpoints({
  endpoints: (builder) => ({
    queryPatients: builder.query<Patient[], PatientFilters>({
      query: (filters) => getPatientsEndpointWithQueryParameters(filters),
      providesTags: ["Patients"],
      transformResponse: (response: PatientsResponse) => {
        return response.results.map(formatPatient);
      }
    }),
    getPatientsPaginated: createPaginatedEndpoint(builder, {
      queryFn: (filters: PatientFilters) =>
        getPatientsEndpointWithQueryParameters(filters),
      transformResponseFn: (response: PatientsResponse) =>
        response.results.map(formatPatient),
      providesTags: ["Patients"]
    }),
    queryPatientsWithEmailsAndPhoneCount: builder.query<
      { count: number; count_sms: number; count_distinct_patients: number },
      PatientFilters
    >({
      query: (filters) =>
        `/patients/patients-count/${formatValidQueryParamUrlCommaSeparated(
          formatPatientFilters(filters),
          true
        )}`,
      providesTags: ["Patients"],
      transformResponse: (response: {
        count: number;
        count_sms: number;
        count_distinct_patients: number;
      }) => ({
        count: Number(response.count),
        count_sms: Number(response.count_sms),
        count_distinct_patients: Number(response.count_distinct_patients)
      })
    }),
    queryPatientsCounter: builder.query<
      number,
      Omit<PatientFilters, "query" | "limit" | "offset">
    >({
      query: (filters) =>
        `/patients/count/?${formatValidQueryParamUrlCommaSeparated(
          formatPatientFilters({
            ...filters,
            baseFilter: {
              is_active: filters?.is_active,
              ...filters.baseFilter
            }
          } as PatientFilters)
        )}`,
      providesTags: ["Patients"]
    }),
    getPatient: builder.query<Patient, { patientId: number; query?: string }>({
      query: ({ patientId, query = "{}" }) =>
        `/patients/${patientId}/?query=${query}`,
      providesTags: (result, _, params) => [
        "Patients",
        {
          type: "Patient" as const,
          id: params.patientId
        }
      ],
      transformResponse: (response: Patient) => formatPatient(response),
      async onQueryStarted(
        { patientId },
        { dispatch, getState, queryFulfilled }
      ) {
        try {
          await queryFulfilled;
          const now = new Date().toISOString();
          const state = getState() as any;
          const queries = state.api?.queries ?? {};
          const targetEndpoints = ["getPatientsPaginated", "queryPatients"];

          for (const entry of Object.values(queries)) {
            const { endpointName, status, originalArgs } = entry as any;
            if (
              !targetEndpoints.includes(endpointName) ||
              status !== "fulfilled" ||
              !originalArgs
            )
              continue;

            const ordering: string | undefined = originalArgs?.ordering;
            const orderingFields = ordering ? ordering.split(",") : [];
            const sortByLastAccessed = orderingFields.some(
              (f) => f === "last_accessed" || f === "-last_accessed"
            );

            const sortPatients = (patients: Patient[]) => {
              if (!sortByLastAccessed) return;
              patients.sort((a: Patient, b: Patient) => {
                for (const field of orderingFields) {
                  const descending = field.startsWith("-");
                  const key = descending ? field.slice(1) : field;
                  const aVal = (a as any)[key];
                  const bVal = (b as any)[key];

                  let cmp = 0;
                  if (aVal == null && bVal == null) cmp = 0;
                  else if (aVal == null) cmp = -1;
                  else if (bVal == null) cmp = 1;
                  else if (
                    typeof aVal === "number" &&
                    typeof bVal === "number"
                  ) {
                    cmp = aVal - bVal;
                  } else {
                    cmp = String(aVal).localeCompare(String(bVal));
                  }

                  if (cmp !== 0) return descending ? -cmp : cmp;
                }
                return 0;
              });
            };

            if (endpointName === "getPatientsPaginated") {
              dispatch(
                patientsApi.util.updateQueryData(
                  "getPatientsPaginated" as any,
                  originalArgs,
                  (draft: any) => {
                    for (const pageKey of Object.keys(draft)) {
                      if (pageKey === "lastFetchedPage") continue;
                      const patient = draft[pageKey]?.find(
                        (p: Patient) => p.id === patientId
                      );
                      if (patient) {
                        patient.last_accessed = now;
                      }
                      sortPatients(draft[pageKey]);
                    }
                  }
                )
              );
            }

            if (endpointName === "queryPatients") {
              dispatch(
                patientsApi.util.updateQueryData(
                  "queryPatients" as any,
                  originalArgs,
                  (draft: any) => {
                    const patient = draft.find(
                      (p: Patient) => p.id === patientId
                    );
                    if (patient) {
                      patient.last_accessed = now;
                    }
                    sortPatients(draft);
                  }
                )
              );
            }
          }
        } catch {
          // Query failed, do nothing
        }
      }
    }),
    // ENG-1949: tagged per patient so the header pill (which does not own the add/delete
    // flows) refreshes when a card is saved or removed. Tilled/JustiFi answer this from the
    // processor, so keep the result for the session rather than RTK's 60s default.
    getPatientCards: builder.query<Card[], number>({
      query: (patientId) => `/patients/${patientId}/cards/`,
      keepUnusedDataFor: 300,
      providesTags: (_result, _error, patientId) => [
        { type: "PatientCards", id: patientId }
      ]
    }),
    // The same list asked from one location's point of view. For a Gravity location the backend
    // returns every location's cards, labelled with where they were saved and whether this location
    // can charge them; for any other provider it answers exactly as `getPatientCards`. Shares the
    // `PatientCards` tag so add/delete/save-during-payment refresh both.
    getPatientCardsAtLocation: builder.query<
      Card[],
      { patientId: number; clinicId: number }
    >({
      query: ({ patientId, clinicId }) =>
        `/patients/${patientId}/cards/?clinic=${clinicId}`,
      keepUnusedDataFor: 300,
      providesTags: (_result, _error, { patientId }) => [
        { type: "PatientCards", id: patientId }
      ]
    }),
    deletePatientCard: builder.mutation<
      Card[],
      { patientId: number; cardId: string }
    >({
      query: ({ patientId, cardId }) => ({
        url: `/patients/${patientId}/cards/${cardId}/`,
        method: "DELETE"
      }),
      invalidatesTags: (_result, _error, { patientId }) => [
        { type: "PatientCards", id: patientId }
      ]
    }),
    createPatientCard: builder.mutation<
      Card,
      { patientId: number; payment_method_id: string }
    >({
      query: ({ patientId, payment_method_id }) => ({
        url: `/patients/${patientId}/cards/`,
        method: "POST",
        body: { payment_method_id }
      }),
      invalidatesTags: (_result, _error, { patientId }) => [
        { type: "PatientCards", id: patientId }
      ]
    }),
    // Gravity/emergepay card-on-file: persist the CreditSaveCard token (uniqueTransId) + display
    // metadata. Mirrors the /cards/ create endpoint's Gravity branch.
    createGravityPatientCard: builder.mutation<
      Card,
      {
        patientId: number;
        unique_trans_id: string;
        last4: string;
        card_type: string;
        exp_month: number;
        exp_year: number;
        holder_name?: string;
        // The Gravity location whose account vaulted the card. Omitted, the backend files it under
        // the patient's home clinic, which is wrong whenever the card was entered elsewhere.
        clinic?: number;
      }
    >({
      query: ({ patientId, ...body }) => ({
        url: `/patients/${patientId}/cards/`,
        method: "POST",
        body
      }),
      invalidatesTags: (_result, _error, { patientId }) => [
        { type: "PatientCards", id: patientId }
      ]
    }),
    createGravityCheckoutSession: builder.mutation<
      { transaction_token: string },
      {
        patientId: number;
        clinic_id: number;
        amount: number | string;
        // AchSaveAccount vault (V4.3) — omitted for cards so the card request body
        // stays byte-identical.
        ach?: boolean;
      }
    >({
      query: ({ patientId, clinic_id, amount, ach }) => ({
        url: `/patients/${patientId}/gravity-checkout-session/`,
        method: "POST",
        // emergepay needs the charge amount at start-transaction time (BE serializer requires it).
        body: { clinic_id, amount, ...(ach ? { ach: true } : {}) }
      })
    }),
    addPatient: builder.mutation<Patient, PatientInput>({
      query: (patientData) => ({
        url: "/patients/",
        method: "POST",
        body: patientData
      }),
      transformResponse: (response: Patient) => formatPatient(response),
      invalidatesTags: ["Patients"]
    }),
    updatePatient: builder.mutation<Patient, Partial<PatientInput>>({
      query: ({ id, ...patientData }) => ({
        url: `/patients/${id}/`,
        method: "PATCH",
        body: patientData
      }),
      transformResponse: (response: Patient) => formatPatient(response),
      invalidatesTags: (result, error, arg, meta) => {
        return error
          ? []
          : ["Patients", "Events", "NoahPatient", "MergeTagsPatient"];
      }
    }),
    deactivatePatient: builder.mutation<
      DeactivatePatientResponse,
      DeactivatePatientInput
    >({
      query: ({ id, ...body }) => ({
        url: `/patients/${id}/deactivate/`,
        method: "PATCH",
        body
      }),
      // Task tags only when tasks were touched, so an ordinary deactivation does not
      // refetch every mounted task list in the app.
      invalidatesTags: (result, error, { cancel_tasks }) =>
        error
          ? []
          : [
              "Patients",
              "Appointments",
              "Events",
              ...(cancel_tasks
                ? ([
                    "StaffTasks",
                    "StaffTasksCount",
                    "PatientStaffTasks"
                  ] as const)
                : [])
            ]
    }),
    updatePatientPhoto: builder.mutation<Patient, PatientPhotoInput>({
      query: ({ id, formData }) => ({
        url: `/patients/${id}/`,
        method: "PATCH",
        body: formData
      }),
      transformResponse: (response: Patient) => formatPatient(response),
      invalidatesTags: ["Patients"]
    }),
    downloadPatientsCSV: builder.query<
      CSVReportResponse,
      PatientsDownloadQuery
    >({
      query: ({ csvHeader = "*", csvLabels, ...filters }) => {
        const url =
          getPatientsEndpointWithQueryParameters(filters) +
          `&csv_header=${csvHeader}&csv_labels=${csvLabels}&current_clinic=${filters.current_clinic}`;

        return {
          url: url,
          headers: { Accept: "custom-csv/json" }
        };
      }
    }),
    getHearingLossSeverityOptions: builder.query<
      HearingLossSeverityOptions,
      void
    >({
      query: () => `/patients/hearing-loss-severity-options/`
    }),
    updatePatientProductsDeliverHearingAids: builder.mutation<
      PatientProduct[],
      {
        patientId: number;
        delivered_at: string;
        update_warranty_dates?: boolean;
        // ENG-2131: per-device dates set in the redelivery modal, keyed by patient product id.
        delivered_at_overrides?: Record<string, string>;
      }
    >({
      query: ({ patientId, ...body }) => ({
        url: `/patients/${patientId}/patient-products/deliver/`,
        method: "POST",
        body
      })
    }),
    // ENG-2131: same endpoint, dry run — says which devices were delivered before and what each
    // one's date would become, so the modal can show it before anything is written.
    previewPatientProductsDelivery: builder.mutation<
      {
        dry_run: boolean;
        devices: {
          id: number;
          name: string | null;
          serial_number: string | null;
          was_delivered_before: boolean;
          statuses: { value: number; created_at: string }[];
          current_delivered_at: string | null;
          proposed_delivered_at: string | null;
        }[];
      },
      {
        patientId: number;
        delivered_at: string;
        update_warranty_dates?: boolean;
      }
    >({
      query: ({ patientId, ...body }) => ({
        url: `/patients/${patientId}/patient-products/deliver/`,
        method: "POST",
        body: { ...body, dry_run: true }
      })
    }),
    deactivatePatients: builder.mutation<void, number[]>({
      query: (ids) => ({
        url: `/patients/bulk-deactivate/`,
        method: "PATCH",
        body: { ids },
        invalidatesTags: ["Patients"]
      })
    }),
    bulkDeactivatePatientsByFilter: builder.mutation<
      { patients_count: number; bulk_action_id: number },
      { patients_filter: Record<string, any> }
    >({
      query: (body) => ({
        url: `/patients/bulk-deactivate-by-filter/`,
        method: "POST",
        body
      })
    }),
    // Additional Bulk Actions: pause marketing / block reviews / contact preferences by
    // filter, optionally stripping and archiving tags in the same run.
    bulkUpdatePatientFieldsByFilter: builder.mutation<
      { patients_count: number; bulk_action_id: number },
      BulkUpdatePatientFieldsInput
    >({
      query: (body) => ({
        url: `/patients/bulk-update-fields-by-filter/`,
        method: "POST",
        body
      })
    }),
    bulkChangePreferredProvider: builder.mutation<
      { patients_count: number; bulk_action_id: number },
      { patients_filter: Record<string, any>; to_provider: number }
    >({
      query: (body) => ({
        url: `/patients/bulk-change-preferred-provider/`,
        method: "POST",
        body
      })
    }),
    bulkChangeReferralSource: builder.mutation<
      { patients_count: number; bulk_action_id: number },
      {
        patients_filter: Record<string, any>;
        referral_source: number;
        // null clears each patient's sub referral source (a sub source pointing
        // at a different parent would be inconsistent data).
        sub_referral_source: number | null;
        // Set only when the destination sub source is a linked Patient/Physician
        // record (EXTENDED_SUB_REFERRAL_SOURCE_FEATURE) — mirrors the patient
        // write model's field pair.
        sub_referral_source_content_type?: string;
        sub_referral_source_object_id?: number;
      }
    >({
      query: (body) => ({
        url: `/patients/bulk-change-referral-source/`,
        method: "POST",
        body
      })
    }),
    mergeDetails: builder.query<
      [PatientMergeDetails, PatientMergeDetails],
      { source_patient_id: number; dest_patient_id: number }
    >({
      query: (params) => ({ url: `/patients/merge-details/`, params })
    }),
    mergePatients: builder.mutation<
      void,
      { source_patient_id: number; dest_patient_id: number }
    >({
      query: (body) => ({
        url: `/patients/merge/`,
        method: "PATCH",
        body,
        invalidatesTags: ["Patients"]
      })
    }),
    addPatientPhysician: builder.mutation<
      void,
      { patientId: number; physicianId: number; physicianType: string }
    >({
      query: ({ patientId, physicianId, physicianType }) => ({
        url: `/patients/${patientId}/other-physician/`,
        method: "POST",
        body: { physician_id: physicianId, physician_type: physicianType }
      }),
      invalidatesTags: ["Patients", "MergeTagsPatient"]
    }),
    removePatientPhysician: builder.mutation<
      void,
      { patientId: number; physicianId: number; physicianType: string }
    >({
      query: ({ patientId, physicianId }) => ({
        url: `/patients/${patientId}/other-physician/?physician_id=${physicianId}`,
        method: "DELETE"
      }),
      invalidatesTags: ["Patients"]
    }),
    getPatientSummary: builder.query<
      PatientSummary | null,
      { patientId: number; type: PatientSummaryType }
    >({
      query: ({ patientId, type }) => ({
        url: `/patients/${patientId}/summary/`,
        params: {
          limit: 1,
          type: type
        }
      }),
      transformResponse: (response: PaginatedResponse<PatientSummary>) => {
        return response.results[0] ?? null;
      },
      providesTags: (result, error, { type }) => [
        { id: type, type: "PatientSummary" }
      ]
    }),
    generatePatientSummary: builder.mutation<
      PatientSummary,
      { patientId: number; type: PatientSummaryType; current_clinic: number }
    >({
      query: ({ patientId, type, current_clinic }) => ({
        url: `/patients/${patientId}/summary/generate/`,
        body: { type },
        params: { current_clinic },
        method: "POST"
      }),
      invalidatesTags: (result, error, { type }) => [
        { id: type, type: "PatientSummary" }
      ]
    }),
    sendCaptionCallLead: builder.mutation<
      void,
      { patientId: number; clinic: number }
    >({
      query: ({ patientId, clinic }) => ({
        url: `/patients/${patientId}/caption-call-leads/`,
        method: "POST",
        body: { clinic }
      }),
      invalidatesTags: ["Patients", "Patient"]
    }),
    getPatientQuickBooksInvoices: builder.query<
      QuickBooksInvoicesResponse,
      { patientId: number; limit?: number }
    >({
      query: ({ patientId, limit = 50 }) => ({
        url: `/patients/${patientId}/quickbooks-invoices/`,
        params: { limit }
      })
    }),
    getPatientQuickBooksDetails: builder.query<
      QuickBooksCustomerDetails | null,
      number
    >({
      query: (patientId) => ({
        url: `/patients/${patientId}/quickbooks-details/`
      }),
      providesTags: (result, error, patientId) => [
        "PatientQuickBooksDetails",
        {
          type: "PatientQuickBooksDetails" as const,
          id: patientId
        }
      ]
    }),
    recordAccess: builder.mutation<void, number>({
      query: (patientId) => ({
        url: `/patients/${patientId}/record-access/`,
        method: "POST"
      })
    }),
    fiveKeysEnroll: builder.mutation<
      { status: string },
      {
        patientId: number;
        email: string;
        fullname: string;
        provider_name: string;
      }
    >({
      query: ({ patientId, ...body }) => ({
        url: `/patients/${patientId}/five-keys-enroll/`,
        method: "POST",
        body
      })
    }),
    searchSimilarPatients: builder.query<
      SimilarPatient[],
      {
        first_name?: string;
        last_name?: string;
        phone?: string;
        email?: string;
        suno_comms_id?: string;
      }
    >({
      query: (params) =>
        `/patients/search-similar/?${formatValidQueryParamUrlCommaSeparated(params)}`
    }),
    getPatientPortalUrl: builder.mutation<
      { url: string },
      { patientId: number; page?: string; clinicId?: number | string | null }
    >({
      query: ({ patientId, page, clinicId }) => ({
        url: `/patients/${patientId}/portal-url/`,
        method: "POST",
        body: {
          ...(page && { page }),
          ...(clinicId && { clinic_id: Number(clinicId) })
        }
      })
    })
  })
});

export interface BulkUpdatePatientFieldsInput {
  patients_filter: Record<string, any>;
  updates: Partial<
    Record<
      | "block_automation"
      | "block_reviews"
      | "do_not_mail"
      | "do_not_email"
      | "do_not_message"
      | "do_not_call",
      boolean
    >
  >;
  remove_tag_ids?: number[];
  archive_empty_tags?: boolean;
  reason?: string;
}

export const {
  useQueryPatientsQuery,
  useQueryPatientsWithEmailsAndPhoneCountQuery,
  useLazyQueryPatientsQuery,
  useLazyQueryPatientsCounterQuery,
  useAddPatientMutation,
  useUpdatePatientMutation,
  useDeactivatePatientMutation,
  useGetPatientQuery,
  useLazyGetPatientQuery,
  useGetPatientCardsQuery,
  useDeletePatientCardMutation,
  useCreatePatientCardMutation,
  useCreateGravityPatientCardMutation,
  useGetPatientCardsAtLocationQuery,
  useCreateGravityCheckoutSessionMutation,
  useUpdatePatientPhotoMutation,
  useLazyDownloadPatientsCSVQuery,
  useGetHearingLossSeverityOptionsQuery,
  useUpdatePatientProductsDeliverHearingAidsMutation,
  usePreviewPatientProductsDeliveryMutation,
  useDeactivatePatientsMutation,
  useBulkDeactivatePatientsByFilterMutation,
  useBulkChangePreferredProviderMutation,
  useBulkUpdatePatientFieldsByFilterMutation,
  useBulkChangeReferralSourceMutation,
  useMergeDetailsQuery,
  useMergePatientsMutation,
  useAddPatientPhysicianMutation,
  useRemovePatientPhysicianMutation,
  useGetPatientSummaryQuery,
  useGeneratePatientSummaryMutation,
  useSendCaptionCallLeadMutation,
  useGetPatientQuickBooksInvoicesQuery,
  useLazyGetPatientQuickBooksInvoicesQuery,
  useGetPatientQuickBooksDetailsQuery,
  useRecordAccessMutation,
  useFiveKeysEnrollMutation,
  useSearchSimilarPatientsQuery,
  useGetPatientPortalUrlMutation
} = patientsApi;
